import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { ElementRef, Injectable, Injector } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { ConnectedAudioPlayerComponent } from '../../components/connected-audio-player/connected-audio-player.component';
import { ConnectedVideoPlayerComponent } from '../../components/connected-video-player/connected-video-player.component';
import { CONNECTED_PLAYER_CLOSE_REQUEST } from '../../tokens/connected-player-close-request';
import { CONNECTED_PLAYER_SRC } from '../../tokens/connected-player-src.token';
import { CONNECTED_PLAYER_VOLUME } from '../../tokens/connected-player-volume.token';

export enum PlayerType {
  Audio,
  Video,
}

export interface OpenPlayerOptions {
  playerType: PlayerType;
  volume: number;
}

const defaultOptions = {
  playerType: PlayerType.Audio,
  volume: 1,
};

export enum ConnectedPlayerEventTypes {
  OPEN = 'OPEN',
  CLOSE = 'CLOSE',
}

export interface ConnectedPlayerEvent {
  kind: ConnectedPlayerEventTypes;
  source: ElementRef | HTMLElement | null;
}

@Injectable({
  providedIn: 'root',
})
/**
 * Remote to display always a single player connected
 * to another component. Used in tables.
 */
export class ConnectedMediaRemoteService {
  private _overlayRef!: OverlayRef;
  private _playerEventsSubject = new Subject<ConnectedPlayerEvent>();

  constructor(private _injector: Injector, private _overlay: Overlay) {}

  public getEvents() {
    return new Observable<ConnectedPlayerEvent>(observer => {
      const subs = this._playerEventsSubject.subscribe(observer);

      return () => {
        subs.unsubscribe();
      };
    });
  }

  public open(
    src: string,
    host: ElementRef | HTMLElement | null,
    options: Partial<OpenPlayerOptions> = {},
  ) {
    this.close();

    const actualOptions: OpenPlayerOptions = {
      ...defaultOptions,
      ...options,
    };

    if (host === null && actualOptions.playerType === PlayerType.Audio) {
      throw new TypeError('Audio players should be connected to a host.');
    }

    switch (actualOptions.playerType) {
      case PlayerType.Audio:
        this.setUpAudioPlayer(
          src,
          host as ElementRef | HTMLElement,
          actualOptions,
        );
        break;
      case PlayerType.Video:
        this.setUpVideoPlayer(src, actualOptions);
        break;
      default:
        throw new TypeError('Unknown PlayerType.');
    }

    this._playerEventsSubject.next({
      kind: ConnectedPlayerEventTypes.OPEN,
      source: host,
    });
  }

  public isOpen() {
    return this._overlayRef.hasAttached();
  }

  public close() {
    if (this._overlayRef) {
      this._overlayRef.dispose();

      this._playerEventsSubject.next({
        kind: ConnectedPlayerEventTypes.CLOSE,
        source: null,
      });
    }
  }

  private setUpAudioPlayer(
    src: string,
    host: ElementRef | HTMLElement,
    options: OpenPlayerOptions,
  ) {
    const domEle =
      host && (host as ElementRef<HTMLElement>).nativeElement
        ? (host as ElementRef<HTMLElement>).nativeElement
        : (host as HTMLElement);

    if (!domEle) {
      throw new TypeError('Invalid host element');
    }

    const providers = new WeakMap();
    providers.set(CONNECTED_PLAYER_SRC, src);
    providers.set(CONNECTED_PLAYER_VOLUME, options.volume);
    const portalInjector = new PortalInjector(this._injector, providers);

    const componentPortal = new ComponentPortal(
      ConnectedAudioPlayerComponent,
      null,
      portalInjector,
    );

    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(domEle)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
        },
        {
          originX: 'end',
          originY: 'top',
          overlayX: 'end',
          overlayY: 'bottom',
        },
      ]);

    const scrollStrategy = this._overlay.scrollStrategies.reposition();

    this._overlayRef = this._overlay.create({
      positionStrategy,
      scrollStrategy,
    });
    return this._overlayRef.attach(componentPortal);
  }

  private setUpVideoPlayer(src: string, options: OpenPlayerOptions) {
    const providers = new WeakMap();
    providers.set(CONNECTED_PLAYER_SRC, src);
    providers.set(CONNECTED_PLAYER_CLOSE_REQUEST, this.close.bind(this));
    providers.set(CONNECTED_PLAYER_VOLUME, options.volume);
    const portalInjector = new PortalInjector(this._injector, providers);

    const componentPortal = new ComponentPortal(
      ConnectedVideoPlayerComponent,
      null,
      portalInjector,
    );

    const positionStrategy = this._overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    const scrollStrategy = this._overlay.scrollStrategies.block();

    this._overlayRef = this._overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: true,
    });

    this._overlayRef.backdropClick().subscribe(() => this.close());

    return this._overlayRef.attach(componentPortal);
  }
}
